import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { useAppSelector, useAppDispatch } from "../hooks";

import { Wave } from "../vendor/better-react-spinkit";
import { map as _map, filter, groupBy, sortBy } from "lodash-es";

import LoginForm from "../components/MainMenu/LoginForm";
import { List, ListItem, ListItemLabel, LabelTitle } from "../components/base/List";
import { Button } from "../components/base/Button";
import InfoDiv from "../components/InfoDiv";

import MainTitle from "../components/MainTitle";
import { logout, login, UserLogin } from "../slices/user";
import { fetchMaps } from "../slices/mapFetch";
import { MapEntity, selectMapEntities } from "../slices/maps";
import store from "src/store";

function MapItem({ name, label, options }: MapEntity) {
  return (
    <ListItem className="border-b-none bg-qmdark-900!">
      <ListItemLabel className=" bg-qmdark-900! border-qmdark-800 border font-light py-0 px-0">
        <Link to={`/map/${name}`} className='px-4 py-2 w-full h-full'>{label}</Link>
      </ListItemLabel>
    </ListItem>
  );
}

function MapGroup({ owner, items }: { owner: string; items: MapEntity[] }) {
  return (
    <ListItem key={owner}>
      <ListItemLabel className="bg-qmdark-900! font-light">
        <LabelTitle className="font-thin">{owner === "null" ? "ACT" : owner}</LabelTitle>
      </ListItemLabel>
      <List className="bg-qmdark-900! space-y-1">
        {_map(items, (item) => (
          <MapItem key={item.id} {...item} />
        ))}
      </List>
    </ListItem>
  );
}

function MapList({ items }: { items: Record<string, MapEntity[]> }) {
  return (
    <List className="bg-qmdark-900! mx-2 px-2 h-[200px]">
      {_map(items, (items, owner) => (
        <MapGroup key={owner} owner={owner} items={items} />
      ))}
    </List>
  );
}

type LocationProps = {
  state: {
    from: string;
  };
};

export function loader() {
  const currentStore = store.getState();
  if (!currentStore.mapFetch.isLoaded) {
    return store.dispatch(fetchMaps(null));
  }
  return null;
}
export function Component() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation() as unknown as LocationProps;
  const maps = useAppSelector(selectMapEntities);
  const user = useAppSelector((state) => state.user);

  const { isLoading: requestingMaps } = useAppSelector(
    (state) => state.mapFetch
  );
  const apiError = useAppSelector((state) => state.mapFetch.error);

  const mapLoadingError = useAppSelector((state) => state.mapState.loadingError);
  const [filterText, setFilterText] = useState("");

  const items = useMemo(() => {
    const filteredItems = filter(
      maps,
      (m) => m?.label?.toLowerCase().indexOf(filterText) !== -1
    );
    console.log(maps)
    const sortedItems = sortBy(filteredItems, ["owner"]);
    return groupBy(sortedItems, "owner");
  }, [maps, filterText]);

  const LoginStatus = (
    <div className="text-right w-full space-x-4 p-1">
      <span className="text-sm">Logged in as {user.username}</span>
      <Button
        variant="secondary"
        isQuiet
        onPress={() => dispatch(logout(null))}
      >
        logout
      </Button>
    </div>
  );

  const loggedIn = user && user.username;

  let menuClass = "MainPage-menu";
  if (!loggedIn) {
    menuClass += " is-login";
  }

  const from = location?.state?.from;

  const handleLogin = React.useCallback(
    (user: UserLogin) => {
      dispatch(login(user))
        .unwrap()
        .then(() => navigate(from, { replace: true, state: { from: null } }));
    },
    [from, dispatch, navigate]
  );
  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundImage: 'url("/img/quickmap-lroc.jpg")' }}
    >
      {loggedIn && LoginStatus}

      <div className="flex-1 w-fit h-fit mx-auto flex flex-col items-center justify-center">
        <MainTitle />
        <div className="flex flex-col border space-y-4 p-8 bg-qmdark-900 mt-4 shadow-md">
          {apiError && (
            <InfoDiv type="error">There was an error loading the list of maps</InfoDiv>
          )}
          {mapLoadingError && (
            <InfoDiv type="error">There was an error loading the map.</InfoDiv>
          )}
          {loggedIn && !requestingMaps && (
            <input
              type="text"
              onChange={(evt) => setFilterText(evt.target.value.toLowerCase())}
              value={filterText}
              className="mx-8"
              placeholder="Filter maps..."
            />
          )}
          {requestingMaps && loggedIn ? (
            <Wave size={40} />
          ) : (
            <div className="flex flex-col flex-1 h-0 max-h-96 w-80 overflow-y-auto">
              <div className={menuClass}>
                {loggedIn ? (
                  <MapList items={items} />
                ) : (
                  <LoginForm onSubmit={handleLogin} errors={user.error} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Component.displayName = "MainMenu";
